/**
 * Insurance Adequacy Calculator
 *
 * Monte Carlo-informed insurance adequacy scoring.
 * Extends the basic assessInsuranceAdequacy() from impactCalibration.js
 * by comparing coverage limits against probability distributions.
 */

// Static reference data — defined at module scope so it isn't re-allocated on every call.

// Available anchors in the simulator output, indexed by exceedance probability.
const ANCHOR_TABLE = [
  { exceed: 0.01, key: 'p99', label: '99th-percentile' },
  { exceed: 0.05, key: 'p95', label: '95th-percentile' },
  { exceed: 0.10, key: 'p90', label: '90th-percentile' },
  { exceed: 0.25, key: 'p75', label: '75th-percentile' },
]

// Market reference ranges by segment (advisory only — never constrains the math).
const MARKET_REFERENCE_RANGES = {
  nano:               { pctLow: 0.10, pctHigh: 0.20, label: 'nano' },
  micro:              { pctLow: 0.08, pctHigh: 0.15, label: 'micro' },
  small:              { pctLow: 0.06, pctHigh: 0.12, label: 'small' },
  'mid-market':       { pctLow: 0.05, pctHigh: 0.10, label: 'mid market' },
  'upper-mid-market': { pctLow: 0.04, pctHigh: 0.08, label: 'upper mid market' },
  enterprise:         { pctLow: 0.02, pctHigh: 0.06, label: 'enterprise' },
}

/**
 * Calculate comprehensive insurance adequacy using Monte Carlo results.
 *
 * @param {Object} impactData - From assessment impact data (coverage limit, deductible, etc.)
 * @param {Object} lossScenarios - From calculateLossScenarios()
 * @param {Object} monteCarloResults - From simulateAggregateExposure()
 * @returns {Object} Detailed adequacy assessment
 */
export function calculateInsuranceAdequacy(impactData = {}, lossScenarios = {}, monteCarloResults = {}) {
  const coverageLimit = parseFloat(impactData.insurance_coverage_limit || impactData.insuranceCoverageLimit) || 0
  const deductible = parseFloat(impactData.client_retention || impactData.deductible) || 10000
  const hasInsurance = impactData.has_cyber_insurance === true || impactData.has_cyber_insurance === 'true' || impactData.hasCyberInsurance === true

  if (!hasInsurance || coverageLimit === 0) {
    return {
      overallAdequacy: 'None',
      coverageScore: 0,
      coverageLimit: 0,
      deductible: 0,
      probabilityOfExceedingCoverage: 1.0,
      expectedAnnualUninsuredExposure: monteCarloResults?.aggregateDistribution?.mean || 0,
      coverageAtPercentiles: {},
      scenarioAdequacy: [],
      gaps: ['No cyber insurance coverage in place'],
      recommendations: [
        { priority: 'critical', action: 'Obtain cyber insurance coverage immediately', estimatedCostIncrease: null }
      ],
      reserveRecommendation: {
        minimumReserve: 50000,
        recommendedReserve: 100000,
        reason: 'Without insurance, the organization bears full financial responsibility for cyber incidents'
      }
    }
  }

  const aggDist = monteCarloResults?.aggregateDistribution?.percentiles || {}
  const scenarios = lossScenarios?.scenarios || []

  // Calculate probability of exceeding coverage
  const exceedanceCurve = monteCarloResults?.exceedanceCurve || []
  const exceedEntry = exceedanceCurve.find(e => e.threshold >= coverageLimit)
  const probabilityOfExceedingCoverage = exceedEntry ? exceedEntry.probability : 0

  // Coverage at percentiles
  const coverageAtPercentiles = {}
  for (const [pKey, pVal] of Object.entries(aggDist)) {
    if (!pVal || typeof pVal !== 'number') continue
    const covered = Math.min(Math.max(0, pVal - deductible), coverageLimit - deductible)
    const gap = Math.max(0, pVal - coverageLimit)
    coverageAtPercentiles[pKey] = {
      grossLoss: pVal,
      covered,
      gap,
      adequate: gap === 0
    }
  }

  // Per-scenario adequacy — uses expected loss (point estimate) for realistic comparison
  const scenarioAdequacy = scenarios.map(s => {
    const expectedLoss = s.grossLoss || 0
    const coveredAmount = Math.min(Math.max(0, expectedLoss - deductible), coverageLimit - deductible)
    const gap = Math.max(0, expectedLoss - coverageLimit)

    return {
      scenarioId: s.scenarioId || s.name?.toLowerCase().replace(/\s+/g, '_'),
      scenarioName: s.scenarioName || s.name || s.scenarioId,
      expectedLoss,
      coveredAmount,
      gap,
      adequate: gap === 0,
      note: gap > 0 ? `Coverage gap of $${gap.toLocaleString()} at expected loss` : null
    }
  })

  // Expected annual uninsured exposure
  const meanLoss = monteCarloResults?.aggregateDistribution?.mean || 0
  const meanCovered = Math.min(Math.max(0, meanLoss - deductible), coverageLimit - deductible)
  const expectedAnnualUninsuredExposure = Math.max(0, meanLoss - meanCovered)

  // ─── Recommended coverage — Monte Carlo P95-anchored, scenario-floored ───
  const annualRevenue = parseFloat(impactData.estimated_annual_revenue || impactData.annualRevenue) || 0

  // Per-client confidence override; default 5% (P95). Clamp to [0.01, 0.20].
  const rawTarget = parseFloat(impactData.recommendation_exceedance_target)
  const exceedanceTarget = Math.min(0.20, Math.max(0.01, Number.isFinite(rawTarget) ? rawTarget : 0.05))

  const snapped = ANCHOR_TABLE.reduce((best, cur) => {
    const dCur = Math.abs(exceedanceTarget - cur.exceed)
    const dBest = Math.abs(exceedanceTarget - best.exceed)
    if (dCur < dBest) return cur
    if (dCur === dBest && cur.exceed < best.exceed) return cur // ties → more conservative
    return best
  }, ANCHOR_TABLE.find(a => a.exceed === 0.01)) // start at p99 (most conservative); reduce finds nearest
  const segment = annualRevenue >= 500000000 ? 'enterprise'
    : annualRevenue >= 100000000 ? 'upper-mid-market'
    : annualRevenue >= 25000000 ? 'mid-market'
    : annualRevenue >= 10000000 ? 'small'
    : annualRevenue >= 5000000 ? 'micro'
    : 'nano'
  const marketRef = MARKET_REFERENCE_RANGES[segment]
  const marketRefLow = annualRevenue > 0 ? Math.round(annualRevenue * marketRef.pctLow) : null
  const marketRefHigh = annualRevenue > 0 ? Math.round(annualRevenue * marketRef.pctHigh) : null

  const aggregateAnchor = aggDist[snapped.key] || 0
  const scenarioFloor = scenarios.length > 0
    ? Math.max(0, ...scenarios.map(s => s.grossLoss || 0))
    : 0

  let recommendedCoverage = null
  let recommendedCoverageBindingAnchor = null  // 'aggregate' | 'scenario' | null
  let recommendedCoverageRationale = null

  if (aggregateAnchor === 0 && scenarioFloor === 0) {
    // No data at all — null recommendation, gap surfaced below.
    recommendedCoverage = null
    recommendedCoverageRationale = null
  } else if (aggregateAnchor === 0) {
    // Scenarios exist but no MC yet — fall back to floor.
    recommendedCoverage = Math.ceil(scenarioFloor / 500000) * 500000
    recommendedCoverageBindingAnchor = 'scenario'
    recommendedCoverageRationale =
      'Based on worst-case single-event exposure; aggregate distribution not yet modeled.'
  } else {
    const raw = Math.max(aggregateAnchor, scenarioFloor)
    recommendedCoverage = Math.ceil(raw / 500000) * 500000
    recommendedCoverageBindingAnchor = aggregateAnchor >= scenarioFloor ? 'aggregate' : 'scenario'

    const fmtUSD = (n) => `$${Math.round(n).toLocaleString('en-US')}`
    const marketRefText = (annualRevenue > 0)
      ? ` Market reference for ${marketRef.label}: ${fmtUSD(marketRefLow)}–${fmtUSD(marketRefHigh)} (typical ${Math.round(marketRef.pctLow * 100)}–${Math.round(marketRef.pctHigh * 100)}% of revenue).`
      : ''

    if (recommendedCoverageBindingAnchor === 'aggregate') {
      let s = `Reflects the ${snapped.label} modeled annual loss (${fmtUSD(aggregateAnchor)}).`
      if (scenarioFloor > 0 && aggregateAnchor > scenarioFloor * 5) {
        s += ' Recommendation is materially higher than the worst single scenario because aggregate annual exposure (multiple incidents per year is plausible) drives the number.'
      }
      s += marketRefText
      recommendedCoverageRationale = s
    } else {
      const worstScenario = scenarios.reduce(
        (best, s) => (s.grossLoss || 0) > (best.grossLoss || 0) ? s : best,
        scenarios[0]
      )
      const worstName = worstScenario?.scenarioName || worstScenario?.scenarioId || 'worst-case'
      let s = `Reflects worst-case single-event exposure (${worstName}, ${fmtUSD(scenarioFloor)}). Aggregate ${snapped.label} modeled loss is ${fmtUSD(aggregateAnchor)}.`
      s += marketRefText
      recommendedCoverageRationale = s
    }
  }

  // ─── Adequacy verdict — anchored on exceedance probability ───────────
  let overallAdequacy = 'Adequate'
  let coverageScore = 100
  if (probabilityOfExceedingCoverage > 0.15) {
    overallAdequacy = 'Inadequate'
  } else if (probabilityOfExceedingCoverage > 0.05) {
    overallAdequacy = 'Partial'
  }
  coverageScore = Math.max(0, Math.min(100, Math.round(100 * (1 - probabilityOfExceedingCoverage))))

  const inadequateScenarios = scenarioAdequacy.filter(s => !s.adequate).length

  // ─── Gaps ────────────────────────────────────────────────────────────
  const gaps = []
  if (recommendedCoverage === null) {
    gaps.push('Insufficient data — complete impact calibration to generate a coverage recommendation.')
  } else if (coverageLimit < recommendedCoverage) {
    const pctExceed = Math.round(probabilityOfExceedingCoverage * 100)
    gaps.push(
      `Current coverage of $${coverageLimit.toLocaleString()} is below the modeled ${snapped.label} annual loss; modeled probability of exceeding current coverage is ${pctExceed}%.`
    )
  }
  if (inadequateScenarios > 0) {
    gaps.push(`${inadequateScenarios} scenario${inadequateScenarios > 1 ? 's have' : ' has'} expected losses that could exceed your current coverage.`)
  }
  if (deductible > 25000) {
    gaps.push(`High deductible ($${deductible.toLocaleString()}) requires significant out-of-pocket reserves before coverage applies.`)
  }

  // Surface review-derived findings: low sublimits and material exclusions
  const sublimits = impactData.insuranceSublimits || {}
  const sublimitValues = Object.entries(sublimits)
    .map(([k, v]) => ({ name: k, value: typeof v === 'number' ? v : parseFloat(v) || 0 }))
    .filter(s => s.value > 0)
  const lowSublimits = sublimitValues.filter(s => s.value < coverageLimit * 0.5)
  if (lowSublimits.length > 0) {
    const sample = lowSublimits.slice(0, 3).map(s => `${s.name} ($${s.value.toLocaleString()})`).join(', ')
    gaps.push(`${lowSublimits.length} sublimit${lowSublimits.length > 1 ? 's are' : ' is'} below 50% of aggregate coverage: ${sample}${lowSublimits.length > 3 ? ' …' : ''}`)
  }
  const exclusions = Array.isArray(impactData.insuranceExclusions) ? impactData.insuranceExclusions : []
  if (exclusions.length > 0) {
    gaps.push(`Policy contains ${exclusions.length} material exclusion${exclusions.length > 1 ? 's' : ''} flagged in the latest review. Validate each against your loss scenarios.`)
  }
  const reviewGaps = impactData.insuranceReviewFindings?.gaps || []
  const highSeverityReviewGaps = reviewGaps.filter(g => (g.severity || '').toLowerCase() === 'high' || (g.severity || '').toLowerCase() === 'critical')
  highSeverityReviewGaps.slice(0, 3).forEach(g => {
    if (g.title) gaps.push(`Policy review: ${g.title}${g.businessImpact ? ` — ${g.businessImpact}` : ''}`)
  })

  // ─── Recommendations ─────────────────────────────────────────────────
  const recommendations = []
  if (recommendedCoverage !== null && coverageLimit < recommendedCoverage) {
    const pctExceed = Math.round(probabilityOfExceedingCoverage * 100)
    recommendations.push({
      priority: probabilityOfExceedingCoverage > 0.15 ? 'high' : 'medium',
      action: `Increase coverage to $${recommendedCoverage.toLocaleString()} to align with the modeled ${snapped.label} annual loss (currently ${pctExceed}% probability of exceeding your limit in any year).`,
      estimatedCostIncrease: null,
    })
  }
  if (inadequateScenarios > 0 && recommendedCoverage !== null && coverageLimit >= recommendedCoverage) {
    recommendations.push({
      priority: 'medium',
      action: 'Review coverage sub-limits and endorsements to ensure all loss categories are adequately addressed.',
      estimatedCostIncrease: null,
    })
  }
  if (deductible > 25000) {
    recommendations.push({
      priority: 'low',
      action: `Evaluate reducing deductible from $${deductible.toLocaleString()} to lower out-of-pocket exposure for smaller incidents.`,
      estimatedCostIncrease: null,
    })
  }

  // Reserve recommendation
  const waitingPeriodCosts = 5000 // Estimated first-response costs during waiting period
  const minimumReserve = deductible + waitingPeriodCosts
  const recommendedReserve = Math.round(minimumReserve * 1.5)

  return {
    overallAdequacy,
    coverageScore,
    coverageLimit,
    deductible,
    probabilityOfExceedingCoverage,
    expectedAnnualUninsuredExposure: Math.round(expectedAnnualUninsuredExposure),
    coverageAtPercentiles,
    scenarioAdequacy,
    gaps,
    recommendations,
    recommendedCoverage,
    recommendedCoverageBindingAnchor,
    recommendedCoverageRationale,
    recommendedCoverageMarketReference: annualRevenue > 0 ? {
      segmentLabel: marketRef.label,
      low: marketRefLow,
      high: marketRefHigh,
      pctLow: marketRef.pctLow,
      pctHigh: marketRef.pctHigh,
    } : null,
    reserveRecommendation: {
      minimumReserve,
      recommendedReserve,
      reason: `Covers $${deductible.toLocaleString()} deductible plus estimated $${waitingPeriodCosts.toLocaleString()} in first-response costs during waiting period`
    }
  }
}
